import React, { useEffect, useState } from 'react';
import {
  downloadIcon,
  downloadFilledIcon,
  closePopupIcon,
  badgeCheckIcon,
} from '../assets/superAdminAssets';
import SideNavigation from '../components/SideNavigation';
import Header from '../components/Header';
import DataTable from '../components/DataTable';
import DatePicker from 'react-datepicker';
import { useAppContext } from '../contexts/AppContext';
import { downloadPaymentAttachment, getAllPaymentsList, getAllPaymentsReport, markPaymentAsPaid } from '../services/payment.service';
import { Constants } from '../constants';
import { formatDollars } from '../helpers/number.helper';
import moment from 'moment';
import { getBoolean } from '../helpers/index.helper';
import Select from 'react-select';

const getStatusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return { background: '#ebffda', color: '#448f48' };
  if (s === 'failed') return { background: '#FFE4E4', color: '#FD151B' };
  return { background: '#f3f4f6', color: '#374151' };
};

const formatPaymentDate = (value) => {
  if (!value || String(value).trim() === '') return '-';
  const d = moment(value, ['M/D/YYYY h:mm:ss A', 'M/D/YYYY', 'YYYY-MM-DD'], true);
  return d.isValid() ? d.format('MMM DD, YYYY') : value;
};

const paymentTypeOptions = [
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

const Payments = () => {
  const { showLoader, hideLoader, showToaster, hideToaster, isLoading } = useAppContext();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [paymentsData, setPaymentsData] = useState([]);
  const [showOfflineTransactionModal, setShowOfflineTransactionModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentTypeError, setPaymentTypeError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  useEffect(() => {
    document.title = 'Loan Mantra - Payments';
    showLoader();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchPayments(page, rowsPerPage, searchText);
    }, 500);
    return () => clearTimeout(timeout);
  }, [page, rowsPerPage, searchText, fromDate, toDate]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const handleOpenSelectedAttachment = () => {
    if (!attachment) return;
    try {
      const url = URL.createObjectURL(attachment);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke URL shortly after opening to free memory
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setAttachment(null);
      setAttachmentError('Attachment is required.');
      return;
    }
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setAttachment(null);
      setAttachmentError('Only PNG, JPG, JPEG images or PDF files are allowed.');
      e.target.value = '';
      return;
    }
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setAttachment(null);
      setAttachmentError('File size must be 10MB or less.');
      e.target.value = '';
      return;
    }
    setAttachment(file);
    setAttachmentError('');
  };

  const handleSaveOfflinePayment = async (paymentId) => {
    try {
      showLoader();
      let valid = true;
      if (!paymentType) {
        setPaymentTypeError('Payment type is required.');
        valid = false;
      } else {
        setPaymentTypeError('');
      }
      if (!attachment) {
        setAttachmentError('Attachment is required.');
        valid = false;
      }
      if (!valid) return;

      const formData = new FormData();
      formData.append('PaymentNo', paymentId);
      formData.append('PaymentMethod', paymentType?.label || '');
      formData.append('Attachment', attachment);
      formData.append('UpdatedBy', 1);

      const res = await markPaymentAsPaid(formData);
      if (res?.status === 200) {
        showToaster(res.message || 'Offline payment saved successfully', 'success');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
        setShowOfflineTransactionModal(false);
        setSelectedPaymentId(null);
        setAttachment(null);
        fetchPayments(page, rowsPerPage, searchText);
      } else {
        showToaster(res.message || 'Failed to save offline payment', 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to save offline payment', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  };

  async function fetchPayments(pageIndex, pageSize, search) {
    try {
      showLoader();
      const res = await getAllPaymentsList({
        PageIndex: pageIndex,
        PageSize: pageSize,
        SearchText: search || '',
        FromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        ToDate: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
      });
      if (res.status === 200) {
        let data = [];
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          data = Array.isArray(parsed) ? parsed : [];
          setTotalCount(Number(res.message?.split(' ')[1]) || data.length);
        } else {
          setTotalCount(0);
        }
        setPaymentsData(data);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch payments', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  async function downloadPaymentReport() {
    try {
      showLoader();
      const res = await getAllPaymentsReport({
        PageIndex: page,
        PageSize: rowsPerPage,
        SearchText: searchText || '',
        FromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        ToDate: toDate ? moment(toDate).format('YYYY-MM-DD') : ''
      });
      if (res.data) {
        const blob = new Blob([res.data], { type: 'application/ms-excel' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `payments-report.xlsx`;
        link.click();
        window.URL.revokeObjectURL(link.href);  // Clean up the URL object
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to download payment report', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }


  async function viewPaymentAttachment(paymentId, isDownload = false) {
    try {
      showLoader();
      const res = await downloadPaymentAttachment(paymentId);
      if (res.data) {
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        if (isDownload) {
          link.download = `payment-attachment-${paymentId}.${res.headers['content-type'].split('/')[1]}`;
        } else {
          link.target = "_blank";
          link.rel = 'noopener noreferrer';
        }
        link.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);    // Clean up the URL object
        }, 0);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to view payment attachment', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 0);
    }
  }

  async function handleDownloadInvoice(payment) {
    try {
      showLoader();
      setTimeout(() => {
        if (getBoolean(payment.IsInvoice)) {
          window.open(payment.InvoiceURL, '_blank');
        } else {
          viewPaymentAttachment(payment.PaymentId, true);
        }
      }, 500);
    } catch (error) {
      console.log(error);
      showToaster('Failed to download invoice', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  return (
    <>
      <SideNavigation />

      <Header
        title="Payments"
      />

      <div className="dashboard-wrap application-detail">
        <div className="p-3">
          <div className="d-flex justify-content-between gap-15 pt-3 flex-wrap">
            <div className="lm-search-bar">
              <input
                style={{ width: '300px' }}
                type="text"
                id="searchInput"
                placeholder="Search by number, name, or other"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setPage(1); }}
              />
            </div>
            <div className="d-flex gap-15 mb-3">
              <div className="form-group d-flex gap-15 align-items-center input-date">
                <label htmlFor="from_date">From</label>
                <DatePicker
                  showIcon
                  toggleCalendarOnIconClick
                  isClearable={!!fromDate}
                  selected={fromDate}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Month DD, YYYY"
                  onChange={(date) => setFromDate(date)}
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  showMonthDropdown
                  showYearDropdown
                  style={{ width: '200px' }}
                />
              </div>
              <div className="form-group d-flex gap-15 align-items-center input-date">
                <label htmlFor="to_date">To</label>
                <DatePicker
                  showIcon
                  toggleCalendarOnIconClick
                  isClearable={!!toDate}
                  selected={toDate}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Month DD, YYYY"
                  onChange={(date) => setToDate(date)}
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  showMonthDropdown
                  showYearDropdown
                  style={{ width: '200px' }}
                />
              </div>
              <a className="lm-button3 mb-3 align-items-center" onClick={downloadPaymentReport}>
                <img className="mr-1 ml-auto" src={downloadFilledIcon} alt="" height="20" width="20" />
                &nbsp;Download report
              </a>
            </div>
          </div>
          <DataTable
            isLoading={isLoading}
            tableId="loanAppTable"
            className="mt-0"
            columns={[
              {
                key: 'PaymentId',
                label: 'Payment ID',
                sortable: true,
                render: (value, row) => (
                  <>
                    <span className="application_no cursor-pointer">{value || '-'}</span>
                    <br />
                    <span className="created_date lm-text-light">{formatPaymentDate(row.Date)}</span>
                  </>
                ),
              },
              { key: 'Companies', label: 'Companies', sortable: true },
              {
                key: 'Amount',
                label: 'Amount',
                sortable: true,
                render: (value) => <span>{value != null && value !== '' ? `$${formatDollars(value)}` : '-'}</span>,
              },
              {
                key: 'Status',
                label: 'Status',
                sortable: true,
                render: (value) => (
                  <div className="status" style={getStatusStyle(value)}>
                    {value || '-'}
                  </div>
                ),
              },
              { key: 'PaymentMethod', label: 'Payment Method', sortable: true },
              {
                key: 'action',
                label: 'Action',
                sortable: false,
                cellClassName: 'action-col',
                render: (_, row) => (
                  <div className="options_btn">
                    <span className="option_dots">...</span>
                    <div id="popoverContent" className="app_options">
                      {
                        row?.Status?.toLowerCase() == 'paid' && (
                          <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadInvoice(row); }}>
                            <img className="me-2" src={downloadIcon} alt="" />Download Invoice
                          </div>
                        )
                      }

                      {/* <div onClick={() => viewPaymentAttachment(row.PaymentId)}>
                        <img className="me-2" src={reviewIcon} alt="" />View Attachment
                      </div> */}

                      {
                        row?.Status?.toLowerCase() != 'paid' && (
                          <div onClick={() => { setShowOfflineTransactionModal(true); setSelectedPaymentId(row.PaymentId); }}>
                            <img className="me-2" src={badgeCheckIcon} alt="" />Mark as Paid
                          </div>
                        )
                      }
                    </div>
                  </div>
                ),
              },
            ]}
            data={paymentsData}
            defaultRowsPerPage={10}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(1); }}
          />
        </div>
      </div>

      {showOfflineTransactionModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowOfflineTransactionModal(false); setSelectedPaymentId(null); setAttachment(null); }}
          ></div>
          <div
            className="modal show d-block fade"
            id="offlineTransationModal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="resendEmailModalLabel"
            aria-hidden="false"
            style={{ zIndex: 1050 }}
            onClick={(e) => {
              // Close modal when clicking outside the modal-content
              if (e.target === e.currentTarget) {
                setShowOfflineTransactionModal(false); setSelectedPaymentId(null); setAttachment(null);
              }
            }}
          >
            <div className="modal-dialog" role="document">

              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="offlineTransationModalLabel">Select Payment Type</h5>
                  <button type="button" className="close border-none p-4" data-dismiss="modal" aria-label="Close" onClick={() => { setShowOfflineTransactionModal(false); setSelectedPaymentId(null); setAttachment(null); }}>
                    <span aria-hidden="true"><img src={closePopupIcon} alt="" /></span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className={`form-group ${paymentTypeError ? 'error-field' : ''}`}>
                        <label htmlFor="payment_type">Payment Type</label>
                        <Select
                          value={paymentType}
                          onChange={(option) => {
                            setPaymentType(option);
                            setPaymentTypeError('');
                          }}
                          inputId="payment_type"
                          classNamePrefix="select"
                          placeholder="Select payment type"
                          options={paymentTypeOptions}
                        />
                        {paymentTypeError && (
                          <span className="error-message">{paymentTypeError}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className={`form-group ${attachmentError ? 'error-field' : ''}`}>
                        <label htmlFor="attachment">Attachment</label>
                        <div className="upload-box">
                          <input
                            type="file"
                            id="attachment"
                            name="attachment"
                            accept="image/png,image/jpeg,application/pdf"
                            style={{
                              opacity: 0,
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '100%',
                              cursor: 'pointer',
                            }}
                            onChange={handleAttachmentChange}
                          />
                          <div className="upload-text">
                            <span>
                              Drop file here or{' '}
                              <button type="button" id="upload-button">
                                browse
                              </button>
                            </span>
                          </div>
                        </div>
                        {attachment && !attachmentError && (
                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-link p-0 application_no cursor-pointer"
                              onClick={handleOpenSelectedAttachment}
                            >
                              {attachment.name}
                            </button>
                          </div>
                        )}
                        {attachmentError && (
                          <span className="error-message">{attachmentError}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="lm-button2"
                    onClick={() => { setShowOfflineTransactionModal(false); setSelectedPaymentId(null); setAttachment(null); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="lm-button1"
                    onClick={() => handleSaveOfflinePayment(selectedPaymentId)}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Payments;
